/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect, useState } from 'react';
import VlogHero from './components/VlogHero';
import VlogGrid from './components/VlogGrid';
import VlogFilters from './components/VlogFilters';
import { useSEOHead } from '../../hooks/useSEOHead';
import { useGenericPageSchema } from '../../hooks/useStructuredData';
import { vlogPosts, vlogCategories } from '../../mocks/vlogPosts';
import { CORE_BUSINESS } from '../../lib/coreSchemas';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import Breadcrumb from '../../components/feature/Breadcrumb';

// Import shared utility from schemaBuilder
function convertDurationToISO8601(duration: string): string {
  const parts = duration.split(':');
  if (parts.length !== 2) return 'PT0S';
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  if (isNaN(minutes) || isNaN(seconds)) return 'PT0S';
  return `PT${minutes}M${seconds}S`;
}

export default function VlogPage() {
  useSEOHead({
    title: '2 Bald Tires - Founders Vlog | Rockhound Auto',
    description: 'Watch the founders of Rockhound Auto share unfiltered insights about the used car industry, dealer tactics, and running an honest dealership.',
    keywords: 'rockhound auto vlog, 2 bald tires, used car dealer vlog, car buying tips videos',
    canonicalUrl: 'https://rockhoundauto.com/vlog',
  });

  // Inject WebPage + BreadcrumbList schema
  useGenericPageSchema({
    name: '2 Bald Tires - Founders Vlog',
    description: 'Watch the founders of Rockhound Auto share unfiltered insights about the used car industry, dealer tactics, and running an honest dealership.',
    url: `${CORE_BUSINESS.url}/vlog`,
    schemaId: 'vlog',
    breadcrumbs: [
      { name: 'Home', item: CORE_BUSINESS.url },
      { name: 'Vlog', item: `${CORE_BUSINESS.url}/vlog` },
    ],
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Vlog',
          item: 'https://rockhoundauto.com/vlog',
        },
      ],
    };

    injectJsonLd('schema-vlog-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-vlog-breadcrumb');
    };
  }, []);

  // Inject VideoObject schema when vlogPosts is non-empty
  useEffect(() => {
    if (!vlogPosts || vlogPosts.length === 0) return;

    const videoListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: '2 Bald Tires - Rockhound Auto Founders Vlog Episodes',
      description: 'Unfiltered insights from the founders of Rockhound Auto about the used car industry, dealer tactics, and running an honest dealership.',
      url: `${CORE_BUSINESS.url}/vlog`,
      numberOfItems: vlogPosts.length,
      itemListElement: vlogPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'VideoObject',
          '@id': `${CORE_BUSINESS.url}/vlog#video-${post.id}`,
          name: post.title,
          description: post.description,
          thumbnailUrl: post.thumbnail,
          uploadDate: new Date(post.publishedAt).toISOString(),
          duration: convertDurationToISO8601(post.duration),
          contentUrl: post.youtubeId
            ? `https://www.youtube.com/watch?v=${post.youtubeId}`
            : undefined,
          embedUrl: post.youtubeId
            ? `https://www.youtube.com/embed/${post.youtubeId}`
            : undefined,
          publisher: {
            '@type': 'Organization',
            name: CORE_BUSINESS.name,
            url: CORE_BUSINESS.url,
            logo: {
              '@type': 'ImageObject',
              url: CORE_BUSINESS.logo,
              width: 400,
              height: 120,
            },
          },
          inLanguage: 'en-US',
          isAccessibleForFree: true,
        },
      })),
    };

    injectJsonLd('schema-vlog-videos', videoListSchema);

    return () => removeJsonLd('schema-vlog-videos');
  }, []);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = vlogPosts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <Breadcrumb items={[{ label: 'Vlog' }]} />
      <VlogHero />

      {vlogPosts.length > 0 && (
        <div className="bg-[#F7F5F0] py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <VlogFilters
              categories={vlogCategories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <div className="mb-2">
              <p className="text-sm text-gray-500">
                {filteredPosts.length}{' '}
                {filteredPosts.length === 1 ? 'episode' : 'episodes'} found
              </p>
            </div>
          </div>
        </div>
      )}

      <VlogGrid posts={filteredPosts} totalPosts={vlogPosts.length} />
    </>
  );
}